import React, { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useSelector } from "react-redux";
import { formatNumber } from "helpers/utils";
import { FaWarehouse, FaArrowCircleUp, FaCheckCircle } from "react-icons/fa";
import { LuShip } from "react-icons/lu";

const METRICS = [
  { key: "opening_balance", label: "Opening Balance", color: "#667eea", gradientEnd: "#764ba2", Icon: FaWarehouse },
  { key: "new_order_intake", label: "New Order Intake", color: "#4facfe", gradientEnd: "#00f2fe", Icon: FaArrowCircleUp },
  { key: "shipment", label: "Shipment", color: "#f093fb", gradientEnd: "#f5576c", Icon: LuShip },
  { key: "closing", label: "Closing", color: "#43e97b", gradientEnd: "#38f9d7", Icon: FaCheckCircle }
];

const formatPointLabel = (value) => {
  if (!value || value < 500) return '';
  return value >= 1000 ? `${(value / 1000).toFixed(1)}K` : Number(value).toFixed(0);
};

const OrderIntakeDashboard = () => {
  const { summaryData } = useSelector((state) => state.crm_shipment_order_intake.allData);
  const [view, setView] = useState("monthly");

  const regionalData = view === "monthly"
    ? summaryData?.regional_monthly
    : summaryData?.regional_yearly;

  const grandTotal = useMemo(() => {
    return regionalData?.find(r => r.region === "GRAND TOTAL") || {};
  }, [regionalData]);

  // Sub-regions only (VIETNAM = SOUTH + NORTH + DANANG, excluded to avoid double-count)
  const chartRegions = useMemo(() => {
    return regionalData?.filter(r => !["GRAND TOTAL", "VIETNAM"].includes(r.region)) || [];
  }, [regionalData]);

  const chartOptions = useMemo(() => {
    if (!chartRegions.length) return {};

    return {
      tooltip: {
        trigger: "axis",
        axisPointerType: "shadow",
        formatter: (params) => {
          if (!Array.isArray(params) || !params.length) return '';
          let html = `<div style="font-weight:600;margin-bottom:6px">${params[0]?.axisValue}</div>`;
          params.forEach(p => {
            html += `<div style="line-height:1.8">${p.marker} ${p.seriesName}: <strong>${formatNumber(p.value)}</strong></div>`;
          });
          return html;
        }
      },
      legend: {
        data: METRICS.map(m => m.label),
        bottom: 5,
        padding: [8, 20],
        textStyle: { fontSize: 11, color: "#64748b" },
        icon: 'circle',
        iconSize: 10
      },
      grid: { top: 30, bottom: 55, left: 55, right: 15 },
      xAxis: {
        type: "category",
        data: chartRegions.map(r => r.region),
        axisLabel: { fontSize: 11, fontWeight: "bold", color: "#1e293b" },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#e2e8f0" } }
      },
      yAxis: {
        type: "value",
        axisLabel: {
          fontSize: 11,
          color: "#64748b",
          formatter: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v.toString()
        },
        splitLine: { lineStyle: { color: "#f0f4f8", type: "dashed" } },
        axisLine: { show: false }
      },
      series: METRICS.map(m => ({
        name: m.label,
        type: "line",
        smooth: true,
        symbolSize: 6,
        symbol: 'circle',
        data: chartRegions.map(r => parseFloat(r[m.key]) || 0),
        lineStyle: { width: 2.5, color: m.color },
        itemStyle: { color: m.color, borderColor: '#fff', borderWidth: 2 },
        areaStyle: { color: m.color, opacity: 0.08 },
        label: {
          show: true,
          position: 'top',
          fontSize: 9,
          color: '#475569',
          distance: 10,
          formatter: (params) => formatPointLabel(params.value)
        }
      })),
      animationDuration: 800,
      animationEasing: 'cubicOut'
    };
  }, [chartRegions]);

  if (!summaryData) return null;

  return (
    <div className="tw-w-full">
      <h5 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Regional Statistics</h5>

      {/* Metrics Grid */}
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-mb-4">
        {METRICS.map(({ key, label, color, gradientEnd, Icon }) => (
          <div key={key} className="tw-bg-white tw-rounded tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow">
            <div className="tw-flex tw-items-start tw-gap-3 tw-p-4">
              <div
                className="tw-flex-shrink-0 tw-w-12 tw-h-12 tw-rounded tw-flex tw-items-center tw-justify-center tw-text-white"
                style={{ background: `linear-gradient(135deg, ${color} 0%, ${gradientEnd} 100%)` }}
              >
                <Icon size={20} />
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-text-lg tw-font-bold tw-text-gray-900 tw-truncate">{formatNumber(grandTotal[key])}</div>
                <div className="tw-text-xs tw-text-gray-600 tw-mt-0.5">{label}</div>
              </div>
            </div>
            <div
              className="tw-h-1"
              style={{ background: `linear-gradient(90deg, ${color} 0%, ${gradientEnd} 100%)` }}
            />
          </div>
        ))}
      </div>

      {/* Chart Card */}
      <div className="tw-bg-white tw-rounded tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
        <div className="tw-p-4">
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-start sm:tw-items-center tw-gap-3 tw-mb-4">
            <small className="tw-text-sm tw-text-gray-600">
              {view === "monthly" ? "Month-to-Date" : "Year-to-Date"} Comparison by Region
            </small>
            <div className="tw-flex tw-items-center tw-gap-1 tw-border tw-border-gray-300 tw-rounded-md tw-p-0.5 tw-bg-gray-50">
              <button
                type="button"
                onClick={() => setView("monthly")}
                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-sm tw-transition-colors ${
                  view === "monthly"
                    ? 'tw-bg-primary-600 tw-text-white'
                    : 'tw-text-gray-600 hover:tw-text-gray-900 tw-bg-transparent'
                }`}
              >
                MTD
              </button>
              <button
                type="button"
                onClick={() => setView("yearly")}
                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-sm tw-transition-colors ${
                  view === "yearly"
                    ? 'tw-bg-primary-600 tw-text-white'
                    : 'tw-text-gray-600 hover:tw-text-gray-900 tw-bg-transparent'
                }`}
              >
                YTD
              </button>
            </div>
          </div>

          {chartRegions.length > 0 && (
            <ReactECharts option={chartOptions} style={{ height: 300 }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderIntakeDashboard;
